import { Model } from 'app/model/backendmodel';
import {
    IResourceFilesProvider,
    ICommitChange,
    IFullResource,
    ResourceProviderLocation
} from 'app/interfaces/IResourceFilesProvider';
import { IResourceBase } from 'app/shared/utils/resourcefiles.utils';
import { stringsAreEqualCaseInsensitive } from 'app/shared/utils/string.util';
import { ModelRegisterationUtils } from 'app/model/services/model.utils/model.registration.utils';
import { ISaveResult } from '../../interfaces/ISaveStatus';
import { BehaviorSubject } from 'rxjs';
import * as Constants from 'app/services/globals';
import { Metamodel, IntegrationRuntime, eModelState, IResourceContext } from 'shell-models-lib';
import { ManagedIntegrationRuntime } from 'app/model/integrationRuntime/managed.integrationRuntime';
import { irDefaultInstanceName } from 'app/integrationruntime/integrationruntime.constants';
import { regions } from 'app/shared/utils/region.util';
import { AutosaveService } from '../autosave/autosave.service';
import { LoggerService } from 'app/logger/logger.service';
import { IAutosaveResourceProvider } from '../autosave/interfaces/IAutosaveResourceProvider';
import { AutosaveResourceProvider } from '../autosave/autosave-resource-provider';
import { SourceState, SourceMarker } from './source-state';
import { IAzureResource } from 'app/interfaces/IAzureResource';
import { IdentityService } from 'app/model/services/identity.service';
import { ExposureControlService } from '../exposure-control/base-exposure-control.service';

export abstract class BaseResourceProvider implements IResourceFilesProvider {
    /** When this subject receives resource arrays, it triggers synchronization in model.service. */
    public resourceSubject: BehaviorSubject<IFullResource[]> = new BehaviorSubject([]);
    protected _initialized = false;
    protected _initializing = false;
    protected _fetchStatusMap: Map<string, boolean> = new Map(); // key is basic/complex resourceType
    protected _fetchingStatusMap: Map<string, boolean> = new Map(); // key is basic/complex resourceType
    protected _resources: Map<string, IFullResource> = new Map<string, IFullResource>(); // Key is the resourceId, whose format is /subscription/.../resourceType/....
    // autosave
    protected _autosaveResourceProvider: IAutosaveResourceProvider;
    private _resourceSourceMap: Map<string, SourceState> = new Map<string, SourceState>(); // Key is the resourceId, whose format is /subscription/.../resourceType/....
    private _className = 'BaseResourceProvider';

    protected constructor(
        protected readonly _contextData: IResourceContext,
        public readonly writeIdentities: boolean,
        public readonly resourceProviderLocation: ResourceProviderLocation,
        protected readonly _logger: LoggerService,
        protected readonly identityService: IdentityService,
        protected readonly _exposureControlService: ExposureControlService,
        protected readonly autosaveService?: AutosaveService
    ) {
        if (this.autosaveService) {
            this._autosaveResourceProvider = new AutosaveResourceProvider(
                this._logger,
                this.autosaveService.autosaveClient
            );
        }
    }

    public async init(fullLoad: boolean, skipPopulateResource: boolean = false): Promise<boolean> {
        if (!this._initialized && !this._initializing) {
            this._initializing = true;

            try {
                await this.onInit();
                if (!skipPopulateResource) {
                    await this.populateAllResources(fullLoad);
                }
                this._initialized = true;
            } catch (error) {
                this._initialized = false;
            } finally {
                this._initializing = false;
            }
        }

        return this._initialized;
    }

    public reset(): void {
        this._initialized = false;
        this.resourceSubject = new BehaviorSubject([]);
        this._resources.clear();
        this._resourceSourceMap.clear();
        if (this._autosaveResourceProvider) {
            this._autosaveResourceProvider.reset();
        }
    }

    public resetResource(
        baseType: Constants.ResourceType,
        parentResourceType?: Constants.ResourceType,
        parentUniqueKey?: string
    ): void {
        const resourceType =
            parentResourceType && parentUniqueKey ? `${parentResourceType}s/${parentUniqueKey}/${baseType}` : baseType;
        this._fetchStatusMap.set(resourceType, false);
        this.resourceSubject = new BehaviorSubject([]);
        this._resourceSourceMap.clear();
    }

    public reinitialize(fullLoad: boolean): Promise<boolean> {
        this._initialized = false;
        return this.init(fullLoad);
    }

    public async reinitializeResource(
        fullLoad: boolean,
        baseType: Constants.ResourceType,
        parentResourceType?: Constants.ResourceType,
        parentUniqueKey?: string
    ): Promise<boolean> {
        const resourceType =
            parentResourceType && parentUniqueKey ? `${parentResourceType}s/${parentUniqueKey}/${baseType}` : baseType;
        if (!this._fetchStatusMap.get(resourceType) && !this._fetchingStatusMap.get(resourceType)) {
            this._fetchingStatusMap.set(resourceType, true);
            try {
                await this.repopulateResourceByType(fullLoad, baseType, parentResourceType, parentUniqueKey);
                this._fetchStatusMap.set(resourceType, true);
            } catch (error) {
                this._fetchStatusMap.set(resourceType, false);
            } finally {
                this._fetchingStatusMap.set(resourceType, false);
            }
        }

        return this._fetchStatusMap.get(resourceType);
    }

    public listResources(...resourceTypes: string[]): Promise<IFullResource[]> {
        return Promise.resolve(
            Array.from(this._resources.values()).filter((resource) => resourceTypes.includes(resource.type))
        );
    }

    public listParentAndChildResources(
        resourceType: Constants.ResourceType,
        parentResourceType?: Constants.ResourceType,
        parentUniqueKey?: string
    ): Promise<IFullResource[]> {
        if (!parentResourceType || !parentUniqueKey) {
            return this.listResources(resourceType);
        }
        const subStringInId = `${parentResourceType}s/${parentUniqueKey}/${resourceType}s/`;
        const subIdentity = this.identityService.getResourceIdentifier(resourceType, parentUniqueKey);
        const result: IFullResource[] = [];
        this._resources.forEach((storedResource: IFullResource, key: string) => {
            if (
                key.toLowerCase().includes(subStringInId.toLowerCase()) ||
                key.toLowerCase().includes(subIdentity.toLowerCase())
            ) {
                result.push(storedResource);
            }
        });
        return Promise.resolve(result);
    }

    public async getResourceJSON(resource: IResourceBase): Promise<string> {
        let stringifiedResource = '';
        let storedResource = this._getExactStoredResource(resource);
        if (!storedResource || storedResource.status === 'Stub') {
            storedResource = await this.getResourceObject(
                resource.type as Constants.ResourceType,
                this.identityService.getUniqueKeyFromResource(resource),
                true,
                {
                    workspaceObjectId: resource.workspaceObjectId,
                    resourceSubType: resource.subType
                }
            );
        }

        if (storedResource) {
            const object = JSON.parse(storedResource.payload);
            delete object.etag;
            delete object.id;

            if (object.properties?.tags) {
                delete object.properties.tags;
            }

            stringifiedResource = JSON.stringify(object);
        }

        return Promise.resolve(stringifiedResource);
    }

    public abstract commit(commitChanges: ICommitChange[], message?: string): Promise<ISaveResult>;

    public abstract getResourceObject(
        resourceType: Constants.ResourceType,
        resourceName: string,
        updateIfExisting?: boolean,
        options?: { workspaceObjectId?: string; resourceSubType?: string }
    ): Promise<IFullResource>;

    public updateStoredResource(options: unknown): void {}

    public getStoredResource(resource: IResourceBase): IFullResource | undefined {
        for (const storedResource of this._resources.values()) {
            if (
                stringsAreEqualCaseInsensitive(
                    this.identityService.getUniqueKeyFromResource(resource),
                    this.identityService.getUniqueKeyFromResource(storedResource)
                ) &&
                stringsAreEqualCaseInsensitive(resource.type, storedResource.type)
            ) {
                return storedResource;
            }
        }

        return undefined;
    }

    public abstract updateEtag(resourceType: Constants.ResourceType, resourceName: string, etag: string): void;

    public async fetchResourcesInUserStore(resourceType: Constants.ResourceType): Promise<IAzureResource[]> {
        // check whether it is enabled to fetch this type of resources from user store
        if (!this.autosaveService || !(await this.autosaveService.isResourceTypeEnabled(resourceType))) {
            this._logger.log(
                AutosaveResourceProvider.className,
                `fetchResourcesInUserStore -  user store for type: ${resourceType} is not enabled`
            );
            return Promise.resolve([]);
        }
        return this._autosaveResourceProvider.fetchResourcesByType(resourceType);
    }

    /**
     * Return a merged resources
     * Merged rule: for a resource
     * 1. Both baseResources and resources contain it, return the value in baseResources
     * 2. Otherwise, return the value
     */
    public mergeResources(
        baseResources: IFullResource[],
        resources: IFullResource[],
        resourceType: Constants.ResourceType
    ): IFullResource[];
    public mergeResources(
        baseResources: IAzureResource[],
        resources: IAzureResource[],
        resourceType: Constants.ResourceType
    ): IAzureResource[];
    public mergeResources(
        baseResources: IAzureResource[] | IFullResource[],
        resources: IAzureResource[] | IFullResource[],
        resourceType: Constants.ResourceType
    ): Array<IFullResource | IAzureResource> {
        this._logger.log(
            this._className,
            `mergeResources - start, base resources count: ${baseResources.length}, resources count: ${resources.length}`
        );

        try {
            const mergedList: Array<IFullResource | IAzureResource> = [];
            const resourceMap: Map<string, IAzureResource | IFullResource> = new Map();
            // generate map for resources, which will be used in merge
            resources.forEach((resource: IAzureResource | IFullResource) => {
                const id = this.identityService.getResourceIdentifier(
                    resourceType,
                    this.identityService.getUniqueKeyFromResource(resource)
                );
                resourceMap.set(id, resource);
            });

            const addToMergedList = (
                candidates: IAzureResource[] | IFullResource[],
                validateFn: (sourceState: SourceState) => boolean,
                generateResource?: (
                    basicResource: IAzureResource | IFullResource,
                    identity: string
                ) => IAzureResource | IFullResource
            ) => {
                candidates.forEach((resource: IAzureResource | IFullResource) => {
                    const id = this.identityService.getResourceIdentifier(
                        resourceType,
                        this.identityService.getUniqueKeyFromResource(resource)
                    );
                    const sourceState = this._resourceSourceMap.get(id);
                    if (validateFn(sourceState)) {
                        let newResource = resource;
                        if (generateResource) {
                            newResource = generateResource(resource, id);
                        }
                        mergedList.push(newResource);
                    }
                });
            };

            this._updateResourcesMap(baseResources, resources, resourceType);

            addToMergedList(
                baseResources,
                (sourceState: SourceState) => {
                    return SourceMarker.isDraft(sourceState);
                },
                (basicResource: IAzureResource | IFullResource, identity: string): IAzureResource | IFullResource => {
                    const resource = resourceMap.get(identity);
                    if (resource) {
                        // only properties of a resource can be editted by the user
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        if ((basicResource as any).properties) {
                            return {
                                ...resource,
                                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                properties: (basicResource as any).properties
                            };
                        } else {
                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                            const basicResourcePayload = JSON.parse((basicResource as any).payload);
                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                            const resourcePayload = JSON.parse((resource as any).payload);
                            return {
                                ...resource,
                                payload: JSON.stringify({
                                    ...resourcePayload,
                                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                    properties: (basicResourcePayload as any).properties
                                })
                            };
                        }
                    }
                    return basicResource;
                }
            );
            addToMergedList(resources, (sourceState: SourceState) => {
                return SourceMarker.isPublished(sourceState) && !SourceMarker.isDraft(sourceState);
            });

            this._logger.log(this._className, `mergeResources - end, merged resources count: ${mergedList.length}`);

            return mergedList;
        } catch (error) {
            this._logger.error(this._className, `mergeResources - error: ${error}`);
        }

        return [];
    }

    /**
     * return the model states
     * @param identity Identity in named {resourceType}$${resourceName}
     */
    public async getResourceStateBySource(
        identity: string,
        resourceType?: Constants.ResourceType
    ): Promise<eModelState[]> {
        // return fetched if autosave service is not ready or the resource type is not supported
        if (
            !this.autosaveService ||
            !resourceType ||
            (resourceType && !(await this.autosaveService.isResourceTypeEnabled(resourceType)))
        ) {
            return [eModelState.FETCHED];
        }

        const sourceState = this._resourceSourceMap.get(identity);
        if (sourceState) {
            const published: boolean = SourceMarker.isPublished(sourceState);
            const privated: boolean = SourceMarker.isDraft(sourceState);
            if (published && !privated) {
                return [eModelState.FETCHED];
            }
            if (published && privated) {
                return [eModelState.FETCHED, eModelState.SAVED, eModelState.DIRTY];
            }
            if (!published && privated) {
                return [eModelState.SAVED, eModelState.DIRTY];
            }
        } else {
            this._logger.log(
                this._className,
                `getResourceStateBySource - source state is empty, identity: ${identity}`
            );
        }

        return [eModelState.FETCHED];
    }

    /**
     * Return renamed name if there is any; Otherwise, return undefined;
     */
    public getRenamedName(resourceType: Constants.ResourceType, originalName: string): string | undefined {
        if (!this.autosaveService || (resourceType && !this.autosaveService.isResourceTypeEnabled(resourceType))) {
            return undefined;
        }

        const fullRenameMap = this._autosaveResourceProvider.renameMap;
        if (fullRenameMap && fullRenameMap.get(resourceType)) {
            const renameMap = fullRenameMap.get(resourceType);
            const currentName = renameMap.get(originalName);
            return currentName;
        }

        return undefined;
    }

    protected onInit(): Promise<void> {
        return Promise.resolve();
    }

    protected abstract populateAllResources(fullLoad: boolean): Promise<void>;

    protected abstract repopulateResourceByType(
        fullLoad: boolean,
        resourceType: Constants.ResourceType,
        parentResourceType?: Constants.ResourceType,
        parentUniqueKey?: string
    ): Promise<void>;

    protected _getExactStoredResource(resource: IResourceBase): IFullResource | undefined {
        const actualResource = ModelRegisterationUtils.replaceSubTypeWithActual(resource);
        for (const storedResource of this._resources.values()) {
            if (
                this.identityService.getUniqueKeyFromResource(actualResource) ===
                    this.identityService.getUniqueKeyFromResource(storedResource) &&
                (actualResource.subType === undefined ||
                    (actualResource.subType &&
                        storedResource.subType &&
                        stringsAreEqualCaseInsensitive(actualResource.subType, storedResource.subType))) &&
                stringsAreEqualCaseInsensitive(actualResource.type, storedResource.type)
            ) {
                return storedResource;
            }
        }
        return undefined;
    }

    protected updateVnetEnabledFlag(irResources: IResourceBase[]): boolean {
        const irModelName = Metamodel.modelFor(IntegrationRuntime).name;
        const managedIrModelName = Metamodel.modelFor(ManagedIntegrationRuntime).name;
        for (const resource of irResources) {
            if (
                resource &&
                resource.type === irModelName &&
                resource.subType === managedIrModelName &&
                resource.name === irDefaultInstanceName
            ) {
                try {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    const managedIR = JSON.parse((resource as any).payload) as Model.IntegrationRuntimeResource;
                    if (
                        managedIR?.properties?.managedVirtualNetwork &&
                        managedIR?.properties?.typeProperties?.computeProperties
                    ) {
                        const isAutoResolve =
                            managedIR.properties.typeProperties.computeProperties.location === regions.AutoResolve;
                        this._exposureControlService.isVNetPresent$.next(isAutoResolve);
                        return isAutoResolve;
                    }
                } catch {
                    // ignore
                }
            }
        }
        return false;
    }

    private _updateResourcesMap(
        baseResources: IAzureResource[] | IFullResource[],
        resources: IAzureResource[] | IFullResource[],
        resourceType: Constants.ResourceType
    ): void {
        baseResources.forEach((resource) => {
            const id = this.identityService.getResourceIdentifier(
                resourceType,
                this.identityService.getUniqueKeyFromResource(resource)
            );
            this._resourceSourceMap.set(id, SourceMarker.markDraft());
        });
        resources.forEach((resource) => {
            const id = this.identityService.getResourceIdentifier(
                resourceType,
                this.identityService.getUniqueKeyFromResource(resource)
            );
            const sourceState = this._resourceSourceMap.get(id);
            this._resourceSourceMap.set(id, SourceMarker.markPublished(sourceState));
        });
    }
}
